import React, { useEffect, useRef } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { ToggleWithLabel } from '../../components/ui';
import { InlineSpinner } from '../../components/common/spinner';
import Toast from '../../components/common/pop-up-message';
import { PageHeader } from '../../components/modals/settings-modal/page-header';
import { FormBlock } from '../../components/modals/settings-modal/form-block';
import useAuth from '../../hooks/utils/useAuth';
import {
  CommsPreferencesResponse,
  useHubspotCommsPreferences,
  useUpdateHubspotCommsPreferences,
} from '../../hooks/api/useCommsPreferences';
import { resolveTheme } from '../../themes';
import { THEME_ID, type ThemeId } from '../../themes/types';

function hubspotDisplayText(text: string, themeId: ThemeId, brandName: string): string {
  if (themeId !== THEME_ID.KINDLY_CHRISTIAN) return text;
  return text.replace(/Kindly Human/g, brandName);
}

export interface NotificationsProps {
  onChange?: () => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

export const Notifications: React.FC<NotificationsProps> = ({ onChange, onSubmittingChange }) => {
  const toastNotificationContainerRef = useRef<HTMLDivElement>(null);
  const theme = resolveTheme();
  const { user, isLoading: userIsLoading } = useAuth();
  const { data: hubspotCommsPreferences, isLoading: hubspotCommsPreferencesIsLoading } = useHubspotCommsPreferences(
    user?.id!,
  );
  const { updateHubspotCommsPreferences } = useUpdateHubspotCommsPreferences(user?.id!);

  const generateDefaultValues = (
    hubspotCommsPreferences: CommsPreferencesResponse | undefined,
  ): CommsPreferencesResponse => {
    if (!hubspotCommsPreferences?.comms_preferences) {
      return { comms_preferences: [] };
    }
    return hubspotCommsPreferences;
  };

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: { isDirty, isSubmitting },
  } = useForm<CommsPreferencesResponse>({
    defaultValues: React.useMemo(() => {
      return generateDefaultValues(hubspotCommsPreferences);
    }, [hubspotCommsPreferences]),
    mode: 'onChange',
  });

  useEffect(() => {
    if (hubspotCommsPreferences) {
      reset(generateDefaultValues(hubspotCommsPreferences));
    }
  }, [hubspotCommsPreferences, reset]);

  useEffect(() => {
    if (onSubmittingChange) {
      onSubmittingChange(isSubmitting);
    }
  }, [isSubmitting, onSubmittingChange]);

  useEffect(() => {
    if (isDirty && onChange) {
      onChange();
    }
  }, [isDirty, onChange]);

  const submit: SubmitHandler<CommsPreferencesResponse> = async (data) => {
    try {
      await updateHubspotCommsPreferences.mutateAsync({
        comms_preferences: data.comms_preferences,
      });
      Toast.success('Profile updated successfully', undefined, toastNotificationContainerRef?.current);
    } catch (error) {
      const currentValues = getValues();
      reset(currentValues);
      Toast.error('Failed to update preferences', undefined, toastNotificationContainerRef?.current);
    }
  };

  const handleToggleChange = (index: number, checked: boolean) => {
    setValue(`comms_preferences.${index}.subscribed`, checked, { shouldDirty: true });
    if (onChange) onChange();
  };

  if (userIsLoading || hubspotCommsPreferencesIsLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <InlineSpinner />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(submit)} className="p-6">
      <div ref={toastNotificationContainerRef} />
      <PageHeader
        header="Notifications"
        buttonText={isSubmitting ? 'Saving...' : 'Save'}
        isButtonDisabled={!isDirty || isSubmitting}
        onButtonClick={handleSubmit(submit)}
        buttonLoading={isSubmitting}
      />
      <div className="space-y-4">
        <FormBlock>
          {hubspotCommsPreferences?.comms_preferences.map((preference, index) => (
            <ToggleWithLabel
              key={preference.id}
              header={hubspotDisplayText(preference.name, theme.id, theme.name)}
              subHeader={hubspotDisplayText(preference.description, theme.id, theme.name)}
              checked={watch(`comms_preferences.${index}.subscribed`)}
              onChange={(checked: boolean) => handleToggleChange(index, checked)}
            />
          ))}
        </FormBlock>
      </div>
    </form>
  );
};
